import {Link, LoaderFunctionArgs, RouteObject} from "react-router";
import Catalog from "./Catalog.tsx";
import React, { useCallback, useMemo } from "react";
import CardPage from "./CardPage.tsx";
import {useAppState} from "../../context/index.ts";
import {CategoryType, ProductType} from "../../types/index";
import {CardPageLoader} from "./CardPageLoader.ts";

export const useCatalogRoutes = (): RouteObject[] => {
    const [categories] = useAppState<CategoryType[]>("categories", []);
    const [filters] = useAppState("catalog-filters", {});

    // Function to serialize filters into a query string

    const CatalogLink = useCallback(() => {
        let query = '';
        if (typeof filters === 'object' && Object.keys(filters).length > 0) {
            query = `filter=${JSON.stringify(filters)}`;
        }
        return <Link to={`/catalog${query ? `?${query}` : ""}`}>
            Catalog
        </Link>
    }, [filters]);

    return React.useMemo<RouteObject[]>(
        () => [
            {
                path: "catalog",
                shouldRevalidate: () => false,
                children: [
                    {
                        handle: {
                            title: 'Catalog',
                            crumb: () => {
                                return <CatalogLink/>;
                            },
                        },
                        element: <Catalog/>,
                        index: true,
                    },
                    {
                        path: ":slug",
                        element: <Catalog/>,
                        loader: ({params: {slug}}: LoaderFunctionArgs) => {
                            if (!slug) return {error: "Slug not provided"}; // Provide default data
                            return categories.find((v) => v.slug === slug) || {error: "Category not found"};
                        },
                        handle: {
                            title: ({data}: { data: CategoryType }) => data?.name ?? 'Loading...',
                            crumb: ({data}: { data: CategoryType }) => {
                                const {slug, name} = data;
                                return [
                                    <CatalogLink/>,
                                    <Link to={`/catalog/${slug}`}>
                                        {name}
                                    </Link>
                                ]
                            },
                        }
                    },
                    {
                        path: "product/:slug",
                        element: <CardPage/>,
                        loader: CardPageLoader,
                        handle: {
                            title: ({data}: { data: ProductType | null }) => {
                                return data?.name ?? 'Loading...';
                            },
                            crumb: ({data}: { data: ProductType | null }) => {
                                if (!data) return <Link to={`/catalog`}>Catalog</Link>;
                                return [
                                    <CatalogLink/>,
                                    <Link to={`/catalog/product/${data.slug}`}>{data.name}</Link>
                                ];
                            },
                        },
                        shouldRevalidate: ({ currentUrl, nextUrl }: { currentUrl: { pathname: string, search: string }, nextUrl: { pathname: string, search: string } }) => {
                            return currentUrl.pathname !== nextUrl.pathname || currentUrl.search !== nextUrl.search;
                        },
                    },
                ],
            },
        ],
        [categories]
    );
};

export default useCatalogRoutes;
